import { SiteListItemView, SiteView, PaginatedSitesApiResponse } from "../shared/services/sites.service";
import { BuildingView } from "../building/services/building.service";
import { addToCache } from "./cache.utils";
import { ApiUrlBuilder, ApiUrlMatcher, InternalUrlBuilder } from "../shared/constants/apiUrl";

// Fonction pour ajouter un log depuis le service worker
const addDebugLog = (message: string) => {
  // Le service worker ne peut pas accéder directement à localStorage
  // On envoie un message au client pour qu'il ajoute le log
  // eslint-disable-next-line no-restricted-globals
  (self as any as ServiceWorkerGlobalScope).clients.matchAll().then((clients) => {
    clients.forEach((client) => {
      client.postMessage({
        type: 'DEBUG_LOG',
        source: 'service-worker',
        message,
        timestamp: Date.now(),
      });
    });
  });
};

export interface SiteHierarchyHooks {
  forSite?: (orga: string, siteId: string, siteCache: Cache) => Promise<unknown>;
  forEachBuilding?: (orga: string, siteId: string, buildingId: string, siteCache: Cache) => Promise<unknown>;
  forEachFloor?: (
    orga: string,
    siteId: string,
    buildingId: string,
    floorId: string,
    siteCache: Cache
  ) => Promise<unknown>;
}

export interface ApplyHook<T> {
  (siteCache: Cache): Promise<T>;
}

const getSiteCache = async (orga: string, siteId: string): Promise<Cache> => {
  const siteCacheKey = ApiUrlBuilder.getSiteCacheKey(orga, siteId);
  return caches.open(siteCacheKey);
};

const deleteSiteCache = async (orga: string, siteId: string) => {
  const siteCacheKey = ApiUrlBuilder.getSiteCacheKey(orga, siteId);
  await caches.delete(siteCacheKey);
};

const allSitesInCache = async (): Promise<{ orga: string; siteId: string }[]> => {
  const cacheKeys = await caches.keys();
  return cacheKeys
    .map((cacheKey) => cacheKey.match(ApiUrlMatcher.siteCacheKey))
    .filter((matchResult): matchResult is RegExpMatchArray => matchResult !== null)
    .map((matchResult) => matchResult.groups!)
    .filter((groups): groups is { orga: string } & { siteId: string } => "siteId" in groups && "orga" in groups);
};

const siteHierarchyTraversal = async (
  orga: string,
  siteId: string,
  siteHierarchyHooks: SiteHierarchyHooks,
  getHierarchyElementFromUrl: (url: string, siteCache: Cache) => Promise<Response>,
  selectedBuildingIds?: string[]
): Promise<void> => {
  console.log(`[Site Hierarchy] Opening cache for site ${siteId}...`);
  const siteCache = await getSiteCache(orga, siteId);
  console.log(`[Site Hierarchy] Cache opened successfully`);

  console.log(`[Site Hierarchy] Fetching site data: ${ApiUrlBuilder.getSite(orga, siteId)}`);
  const site = await getHierarchyElementFromUrl(ApiUrlBuilder.getSite(orga, siteId), siteCache);
  const siteData = (await site!.json()) as SiteView;
  console.log(`[Site Hierarchy] Site "${siteData.name}" loaded with ${siteData.buildings.length} building(s)`);
  
  if (siteHierarchyHooks.forSite) {
    console.log(`[Site Hierarchy] Executing forSite hook...`);
    await siteHierarchyHooks.forSite(orga, siteId, siteCache);
    console.log(`[Site Hierarchy] forSite hook completed`);
  }

  // Traiter les bâtiments seulement si selectedBuildingIds contient des IDs
  // Si selectedBuildingIds est undefined ou un tableau vide, on ne traite aucun bâtiment
  const buildingsToProcess = selectedBuildingIds && selectedBuildingIds.length > 0
    ? siteData.buildings.filter(building => selectedBuildingIds.includes(building.id))
    : []; // undefined ou tableau vide = aucun bâtiment à traiter
  
  console.log(`[Site Hierarchy] Processing ${buildingsToProcess.length} building(s) out of ${siteData.buildings.length} total`);

  // Ne traiter les bâtiments que s'il y en a à traiter
  if (buildingsToProcess.length > 0) {
    await Promise.all(
      buildingsToProcess.map(async (buildingItem) => {
      console.log(`[Building ${buildingItem.id}] Downloading building data...`);
      const building = await getHierarchyElementFromUrl(ApiUrlBuilder.getBuilding(orga, buildingItem.id), siteCache);
      const buildingData = (await building!.json()) as BuildingView;
      console.log(`[Building ${buildingItem.id}] Building "${buildingData.name}" downloaded, processing ${buildingData.floors.length} floor(s)...`);
      
      await siteHierarchyHooks.forEachBuilding?.(orga, siteId, buildingItem.id, siteCache);

      await Promise.all(
        buildingData.floors.map(async (floorItem) => {
          console.log(`[Building ${buildingItem.id}] [Floor ${floorItem.id}] Downloading floor data...`);
          await getHierarchyElementFromUrl(ApiUrlBuilder.getFloor(orga, floorItem.id), siteCache);
          await siteHierarchyHooks.forEachFloor?.(orga, siteId, buildingItem.id, floorItem.id, siteCache);
          console.log(`[Building ${buildingItem.id}] [Floor ${floorItem.id}] Floor downloaded`);
        })
      );
      console.log(`[Building ${buildingItem.id}] Building "${buildingData.name}" download completed`);
      })
    );
  } else {
    console.log(`[Site Hierarchy] No buildings to process (only outdoor areas will be processed)`);
  }
};

const goOffline = async (orga: string, siteId: string, siteHierarchyHooks: SiteHierarchyHooks, selectedBuildingIds?: string[]): Promise<void> => {
  await siteHierarchyTraversal(orga, siteId, siteHierarchyHooks, addToCache, selectedBuildingIds);
};

// Supprimer toutes les données d'un bâtiment du cache (building, floors, annotations, attributes, données offline)
const deleteBuildingDataFromCache = async (orga: string, siteId: string, buildingId: string): Promise<void> => {
  const siteCache = await getSiteCache(orga, siteId);
  
  // Récupérer le bâtiment pour obtenir la liste des floors
  const buildingUrl = ApiUrlBuilder.getBuilding(orga, buildingId);
  const buildingResponse = await siteCache.match(buildingUrl);
  
  if (buildingResponse) {
    const building = await buildingResponse.json() as BuildingView;
    const floors = building.floors || [];
    
    console.log(`[Delete Building Data] Removing all data for building ${buildingId} (${floors.length} floor(s))...`);
    
    // Supprimer le bâtiment lui-même
    await siteCache.delete(buildingUrl);
    console.log(`[Delete Building Data] Building ${buildingId} removed`);
    
    // Supprimer tous les floors du bâtiment
    for (const floor of floors) {
      const floorUrl = ApiUrlBuilder.getFloor(orga, floor.id);
      await siteCache.delete(floorUrl);
      console.log(`[Delete Building Data] Floor ${floor.id} removed`);
      
      // Supprimer les annotations du floor
      const annotationsUrl = ApiUrlBuilder.getAnnotations(orga, siteId, buildingId, floor.id);
      await siteCache.delete(annotationsUrl);
      
      // Supprimer les attributs du floor
      const attributesUrl = ApiUrlBuilder.getAttributes(orga, siteId, buildingId, floor.id, "");
      await siteCache.delete(attributesUrl);
      
      // Supprimer les données offline du floor
      const offlineCreatedAnnotationsUrl = InternalUrlBuilder.getOfflineCreatedAnnotations(orga, siteId, buildingId, floor.id);
      await siteCache.delete(offlineCreatedAnnotationsUrl);
      
      const offlineUpdatedAnnotationsUrl = InternalUrlBuilder.getOfflineUpdatedExistingAnnotations(orga, siteId, buildingId, floor.id);
      await siteCache.delete(offlineUpdatedAnnotationsUrl);
      
      const offlineDeletedAnnotationsUrl = InternalUrlBuilder.getOfflineDeletedAnnotations(orga, siteId, buildingId, floor.id);
      await siteCache.delete(offlineDeletedAnnotationsUrl);
      
      const offlineUpdatedAttributesUrl = InternalUrlBuilder.getOfflineUpdatedAttributes(orga, siteId, buildingId, floor.id);
      await siteCache.delete(offlineUpdatedAttributesUrl);
      
      console.log(`[Delete Building Data] Floor ${floor.id} data (annotations, attributes, offline data) removed`);
    }
    
    console.log(`[Delete Building Data] All data for building ${buildingId} removed`);
  } else {
    console.log(`[Delete Building Data] Building ${buildingId} not found in cache, nothing to remove`);
  }
};

// Vérifier si un site a encore des bâtiments offline (ou l'espace extérieur)
const hasRemainingOfflineContent = async (orga: string, siteId: string): Promise<boolean> => {
  try {
    const siteCache = await getSiteCache(orga, siteId);
    
    // Vérifier si l'espace extérieur est encore offline
    const outdoorAreasPlanUrl = ApiUrlBuilder.getOutdoorAreasPlan(orga, siteId);
    const cachedPlan = await siteCache.match(outdoorAreasPlanUrl);
    if (cachedPlan) {
      console.log(`[Has Remaining Offline Content] Site ${siteId} still has outdoor areas offline`);
      return true;
    }
    
    // Vérifier si le site lui-même est dans le cache
    const siteUrl = ApiUrlBuilder.getSite(orga, siteId);
    const siteResponse = await siteCache.match(siteUrl);
    if (!siteResponse) {
      return false;
    }
    
    const site = await siteResponse.json() as SiteView;
    const buildings = site.buildings || [];
    
    // Vérifier si au moins un bâtiment est encore dans le cache
    for (const building of buildings) {
      const buildingUrl = ApiUrlBuilder.getBuilding(orga, building.id);
      const buildingResponse = await siteCache.match(buildingUrl);
      if (buildingResponse) {
        console.log(`[Has Remaining Offline Content] Site ${siteId} still has building ${building.id} offline`);
        return true;
      }
    }
    
    console.log(`[Has Remaining Offline Content] Site ${siteId} has no remaining offline content`);
    return false;
  } catch (e) {
    console.error(`[Has Remaining Offline Content] Error checking site ${siteId}:`, e);
    return false;
  }
};

const sync = async (orga: string, siteId: string, siteHierarchyHooks: SiteHierarchyHooks, selectedBuildingIds?: string[], syncOutdoorAreas: boolean = true): Promise<void> => {
  const logMsg1 = `[Site Sync] Starting synchronization for site ${siteId} (buildings: ${selectedBuildingIds ? selectedBuildingIds.length : 'all'}, outdoor areas: ${syncOutdoorAreas})`;
  console.log(logMsg1);
  addDebugLog(logMsg1);
  
  await siteHierarchyTraversal(
    orga,
    siteId,
    siteHierarchyHooks,
    async (url, siteCache) => (await siteCache.match(url))!,
    selectedBuildingIds
  );
  
  const logMsg2 = `[Site Sync] Synchronization traversal completed for site ${siteId}`;
  console.log(logMsg2);
  addDebugLog(logMsg2);
  
  // Si on synchronise tous les bâtiments ET l'espace extérieur, supprimer le cache du site
  const allBuildingsSynced = !selectedBuildingIds || selectedBuildingIds.length === 0;
  if (allBuildingsSynced && syncOutdoorAreas) {
    const logMsg3 = `[Site Sync] All buildings and outdoor areas synchronized, deleting site cache...`;
    console.log(logMsg3);
    addDebugLog(logMsg3);
    await deleteSiteCache(orga, siteId);
    const logMsg4 = `[Site Sync] Site cache deleted`;
    console.log(logMsg4);
    addDebugLog(logMsg4);
  } else {
    const logMsg5 = `[Site Sync] Partial synchronization completed`;
    console.log(logMsg5);
    addDebugLog(logMsg5);
    const siteCache = await getSiteCache(orga, siteId);
    
    // Supprimer toutes les données des bâtiments synchronisés
    if (selectedBuildingIds && selectedBuildingIds.length > 0) {
      const logMsg6 = `[Site Sync] Removing all data for ${selectedBuildingIds.length} synchronized building(s): ${selectedBuildingIds.join(', ')}`;
      console.log(logMsg6);
      addDebugLog(logMsg6);
      for (const buildingId of selectedBuildingIds) {
        await deleteBuildingDataFromCache(orga, siteId, buildingId);
        const logMsg7 = `[Site Sync] Building ${buildingId} data removed from cache`;
        console.log(logMsg7);
        addDebugLog(logMsg7);
      }
      const logMsg8 = `[Site Sync] Synchronized buildings data removed`;
      console.log(logMsg8);
      addDebugLog(logMsg8);
    }
    
    // Supprimer le cache de l'espace extérieur si synchronisé
    if (syncOutdoorAreas) {
      const logMsg9 = `[Site Sync] Removing outdoor areas cache and offline data...`;
      console.log(logMsg9);
      addDebugLog(logMsg9);
      
      const outdoorAreasPlanUrl = ApiUrlBuilder.getOutdoorAreasPlan(orga, siteId);
      await siteCache.delete(outdoorAreasPlanUrl);
      
      // Supprimer aussi les données offline de l'espace extérieur
      const offlineCreatedAnnotationsUrl = InternalUrlBuilder.getOfflineCreatedAnnotations(orga, siteId, siteId, siteId);
      await siteCache.delete(offlineCreatedAnnotationsUrl);
      
      const offlineUpdatedAnnotationsUrl = InternalUrlBuilder.getOfflineUpdatedExistingAnnotations(orga, siteId, siteId, siteId);
      await siteCache.delete(offlineUpdatedAnnotationsUrl);
      
      const offlineDeletedAnnotationsUrl = InternalUrlBuilder.getOfflineDeletedAnnotations(orga, siteId, siteId, siteId);
      await siteCache.delete(offlineDeletedAnnotationsUrl);
      
      const offlineUpdatedAttributesUrl = InternalUrlBuilder.getOfflineUpdatedAttributes(orga, siteId, siteId, siteId);
      await siteCache.delete(offlineUpdatedAttributesUrl);
      
      const annotationsUrl = ApiUrlBuilder.getAnnotations(orga, siteId, null, null);
      await siteCache.delete(annotationsUrl);
      
      const attributesUrl = ApiUrlBuilder.getAttributes(orga, siteId, siteId, siteId, "");
      await siteCache.delete(attributesUrl);
      
      const logMsg10 = `[Site Sync] Outdoor areas cache and offline data removed`;
      console.log(logMsg10);
      addDebugLog(logMsg10);
    }
    
    // Vérifier s'il reste du contenu offline pour ce site
    const hasRemaining = await hasRemainingOfflineContent(orga, siteId);
    if (!hasRemaining) {
      const logMsg11 = `[Site Sync] No remaining offline content, deleting entire site cache...`;
      console.log(logMsg11);
      addDebugLog(logMsg11);
      await deleteSiteCache(orga, siteId);
      const logMsg12 = `[Site Sync] Site cache deleted (no remaining offline content)`;
      console.log(logMsg12);
      addDebugLog(logMsg12);
    } else {
      const logMsg13 = `[Site Sync] Site still has offline content, keeping site cache`;
      console.log(logMsg13);
      addDebugLog(logMsg13);
    }
  }
  const logMsg14 = `[Site Sync] Synchronization completed for site ${siteId}`;
  console.log(logMsg14);
  addDebugLog(logMsg14);
};

const apply = async <T>(orga: string, siteId: string, applyHook: ApplyHook<T>): Promise<T> => {
  const siteCache = await getSiteCache(orga, siteId);
  return applyHook(siteCache);
};

const isOffline = async (orga: string, siteId: string): Promise<boolean> => {
  const offlineSites = await allSitesInCache();
  return offlineSites.some((offlineSite) => offlineSite.orga === orga && offlineSite.siteId === siteId);
};

const isBuildingOffline = async (orga: string, siteId: string, buildingId: string): Promise<boolean> => {
  // Vérifier d'abord si le site est offline
  const siteIsOffline = await isOffline(orga, siteId);
  if (!siteIsOffline) {
    return false;
  }
  
  // Vérifier si le bâtiment est dans le cache
  try {
    const siteCache = await getSiteCache(orga, siteId);
    const buildingUrl = ApiUrlBuilder.getBuilding(orga, buildingId);
    const cachedBuilding = await siteCache.match(buildingUrl);
    return cachedBuilding !== undefined;
  } catch (e) {
    return false;
  }
};

const isOutdoorAreasOffline = async (orga: string, siteId: string): Promise<boolean> => {
  // Vérifier d'abord si le site est offline
  const siteIsOffline = await isOffline(orga, siteId);
  if (!siteIsOffline) {
    return false;
  }
  
  // Vérifier si le plan du site (espace extérieur) est dans le cache
  try {
    const siteCache = await getSiteCache(orga, siteId);
    const outdoorAreasPlanUrl = ApiUrlBuilder.getOutdoorAreasPlan(orga, siteId);
    const cachedPlan = await siteCache.match(outdoorAreasPlanUrl);
    return cachedPlan !== undefined;
  } catch (e) {
    return false;
  }
};

const handleGetSiteOffline = async (request: Request): Promise<Response> => {
  const response = await caches.match(request);
  return enrichGetSiteResponseWithOfflineStatus(request, response!);
};

const enrichGetSiteResponseWithOfflineStatus = async (request: Request, response: Response): Promise<Response> => {
  const orga = request.url.match(ApiUrlMatcher.getSite)!.groups!.orga;
  const site = (await response.json()) as SiteView;
  const enrichedSite = {
    ...site,
    isOffline: await isOffline(orga, site.id),
  };
  return new Response(JSON.stringify(enrichedSite));
};

// Vérifier si un site a au moins un bâtiment ou l'espace extérieur téléchargé
export const hasDownloadedContent = async (orga: string, siteId: string): Promise<boolean> => {
  try {
    const siteCache = await getSiteCache(orga, siteId);
    
    // Vérifier si l'espace extérieur est téléchargé
    const outdoorAreasPlanUrl = ApiUrlBuilder.getOutdoorAreasPlan(orga, siteId);
    const cachedPlan = await siteCache.match(outdoorAreasPlanUrl);
    if (cachedPlan) {
      console.log(`[hasDownloadedContent] Site ${siteId} has outdoor areas downloaded`);
      return true;
    }
    
    // Vérifier si le site lui-même est dans le cache
    const siteUrl = ApiUrlBuilder.getSite(orga, siteId);
    const siteResponse = await siteCache.match(siteUrl);
    if (siteResponse) {
      const siteData = (await siteResponse.json()) as SiteView;
      // Vérifier si au moins un bâtiment est dans le cache
      for (const building of siteData.buildings) {
        const buildingUrl = ApiUrlBuilder.getBuilding(orga, building.id);
        const cachedBuilding = await siteCache.match(buildingUrl);
        if (cachedBuilding) {
          console.log(`[hasDownloadedContent] Site ${siteId} has building ${building.id} downloaded`);
          return true;
        }
      }
    }
    
    console.log(`[hasDownloadedContent] Site ${siteId} has no downloaded content`);
    return false;
  } catch (e) {
    console.error(`[hasDownloadedContent] Error checking downloaded content for site ${siteId}:`, e);
    return false;
  }
};

// Gérer la requête de liste de sites en mode offline
// Cette fonction n'est appelée que si navigator.onLine = false
const handleGetSitesOffline = async (request: Request): Promise<Response> => {
  // Extraire l'organisation depuis l'URL
  const orgaMatch = request.url.match(ApiUrlMatcher.getSites);
  if (!orgaMatch || !orgaMatch.groups) {
    // Si on ne peut pas extraire l'organisation, retourner une liste vide
    const emptyResponse: PaginatedSitesApiResponse = {
      items: [],
      page: 1,
      pageSize: 0,
      totalPages: 1,
      totalItems: 0,
      hasMore: false
    };
    return new Response(JSON.stringify(emptyResponse));
  }
  
  const orga = orgaMatch.groups.orga;
  const logMsg1 = `[handleGetSitesOffline] Fetching offline sites for organization ${orga}...`;
  console.log(logMsg1);
  addDebugLog(logMsg1);
  
  // Récupérer tous les sites en cache pour cette organisation
  const offlineSites = await allSitesInCache();
  const orgOfflineSites = offlineSites.filter(site => site.orga === orga);
  const logMsg2 = `[handleGetSitesOffline] Found ${orgOfflineSites.length} site(s) in cache for organization ${orga}`;
  console.log(logMsg2);
  addDebugLog(logMsg2);
  
  // Filtrer les sites qui ont au moins un bâtiment ou l'espace extérieur téléchargé
  const sitesWithContent: { orga: string; siteId: string }[] = [];
  for (const { siteId } of orgOfflineSites) {
    const hasContent = await hasDownloadedContent(orga, siteId);
    if (hasContent) {
      sitesWithContent.push({ orga, siteId });
    }
  }
  const logMsg3 = `[handleGetSitesOffline] ${sitesWithContent.length} site(s) have downloaded content`;
  console.log(logMsg3);
  addDebugLog(logMsg3);
  
  // Récupérer les données des sites depuis le cache
  const cachedSiteViews: SiteView[] = await Promise.all(
    sitesWithContent.map(async ({ siteId }) => {
      const siteCache = await getSiteCache(orga, siteId);
      const siteUrl = ApiUrlBuilder.getSite(orga, siteId);
      const response = await siteCache.match(siteUrl);
      if (!response) {
        throw new Error(`Site ${siteId} not found in cache`);
      }
      return response.json();
    })
  );
  
  const siteList: SiteListItemView[] = cachedSiteViews.map((siteResponse) => ({
    id: siteResponse.id,
    name: siteResponse.name,
    address: siteResponse.address,
    numberOfAnnotations: siteResponse.numberOfAnnotations,
    numberOfAnnotationsOnArea: siteResponse.numberOfAnnotationsOnArea,
    isOffline: true,
    isGraphic: siteResponse.isGraphic,
    isExported: siteResponse.isExported,
    numberOfExportedBuildings: siteResponse.numberOfExportedBuildings,
    numberOfBuildings: siteResponse.numberOfBuildings
  }));
  
  const logMsg4 = `[handleGetSitesOffline] Returning ${siteList.length} site(s) for organization ${orga}`;
  console.log(logMsg4);
  addDebugLog(logMsg4);
  
  // Retourner directement un tableau pour être cohérent avec l'API
  // (l'API retourne un tableau, pas un objet paginé)
  return new Response(JSON.stringify(siteList), {
    headers: { 'Content-Type': 'application/json' }
  });
};

const enrichGetSitesResponseWithOfflineStatus = async (request: Request, response: Response): Promise<Response> => {
  const orga = request.url.match(ApiUrlMatcher.getSites)!.groups!.orga;
  const originalData = await response.json();
  
  const logMsg1 = `[enrichGetSitesResponseWithOfflineStatus] Processing response for org: ${orga}`;
  console.log(logMsg1);
  addDebugLog(logMsg1);
  
  // Gérer les réponses paginées (avec items) ou les tableaux directs
  const sitesArray: SiteListItemView[] = Array.isArray(originalData) 
    ? originalData 
    : (originalData && Array.isArray(originalData.items) ? originalData.items : []);
  
  const logMsg2 = `[enrichGetSitesResponseWithOfflineStatus] Found ${sitesArray.length} site(s) in response`;
  console.log(logMsg2);
  addDebugLog(logMsg2);
  
  // Récupérer tous les sites hors ligne
  const offlineSites = await allSitesInCache();
  const logMsg3 = `[enrichGetSitesResponseWithOfflineStatus] Found ${offlineSites.length} offline site(s) in cache`;
  console.log(logMsg3);
  addDebugLog(logMsg3);
  
  // Pour chaque site dans la liste
  const newSiteList: SiteListItemView[] = await Promise.all(
    sitesArray.map(async (site) => {
      // Vérifier si le site est hors ligne
      const isOffline = offlineSites.some(offlineSite => offlineSite.orga === orga && offlineSite.siteId === site.id);
      
      if (isOffline) {
        // Si le site est hors ligne, récupérer ses données du cache
        const siteResponse = await caches.match(ApiUrlBuilder.getSite(orga, site.id));
        if (siteResponse) {
          const siteData = await siteResponse.json() as SiteView;
          return {
            ...site,
            isOffline: true,
            numberOfAnnotations: siteData.numberOfAnnotations
          };
        }
      }
      
      // Si le site n'est pas hors ligne ou si on n'a pas trouvé ses données en cache
      return {
        ...site,
        isOffline: isOffline
      };
    })
  );
  
  const logMsg4 = `[enrichGetSitesResponseWithOfflineStatus] Returning ${newSiteList.length} site(s) after enrichment`;
  console.log(logMsg4);
  addDebugLog(logMsg4);
  
  // Si la réponse originale était un tableau, retourner un tableau
  if (Array.isArray(originalData)) {
    return new Response(JSON.stringify(newSiteList), {
      headers: response.headers,
      status: response.status,
    });
  }
  
  // Sinon, retourner la réponse paginée enrichie
  const enrichedResponse: PaginatedSitesApiResponse = {
    ...(originalData as PaginatedSitesApiResponse),
    items: newSiteList
  };
  
  return new Response(JSON.stringify(enrichedResponse), {
    headers: response.headers,
    status: response.status,
  });
};

export const Sites = {
  goOffline,
  sync,
  apply,
  isOffline,
  isBuildingOffline,
  isOutdoorAreasOffline,
  handleGetSiteOffline,
  enrichGetSiteResponseWithOfflineStatus,
  handleGetSitesOffline,
  enrichGetSitesResponseWithOfflineStatus,
  hasDownloadedContent,
};
